import { Locale } from "@omni/types/enums/locale.enum";

import { Config } from "@/utils/config.utils";
import { formatNumber } from "@/utils/format";

import { UnitRangeFacetValue } from "../../@types/facetType.types";
import { RefinementSort } from "../../facetSortBy";
import {
    ConversionUnit,
    ConversionUnitType,
    convert,
    getPreferredUnitForValue,
    toUnitRangeUnit
} from "./unitRange.config";
import { UnitRangeFacetDisplayValue } from "./unitRangeFacet.container";

const formatAsLocaleNumber = (value: number, locale: Locale) => {
    const roundedValue = Math.trunc(value * 10) / 10;

    return formatNumber(roundedValue, locale);
};

export const formatUnitRangeLabel = (
    { unit, lowerBound, upperBound }: UnitRangeFacetValue,
    locale: Locale
) => {
    const localeLowerBound = formatAsLocaleNumber(lowerBound, locale);
    const localeUpperBound = formatAsLocaleNumber(upperBound, locale);

    return `${localeLowerBound} - ${localeUpperBound} ${unit}`;
};

export const sortUnitRange = (
    a: UnitRangeFacetDisplayValue,
    b: UnitRangeFacetDisplayValue
) => {
    const isRefinedSort = RefinementSort.IS_REFINED_DESC(a, b);
    if (isRefinedSort) return isRefinedSort;

    return a.lowerBoundMillimetres - b.lowerBoundMillimetres;
};

export const mapToFacetValueInMillimetres = (
    facetValue: UnitRangeFacetValue
): UnitRangeFacetValue & {
    lowerBoundMillimetres: number;
    upperBoundMillimetres: number;
    initialUnit: ConversionUnitType;
} => {
    const unit = toUnitRangeUnit(facetValue.unit);

    return {
        ...facetValue,
        lowerBoundMillimetres: convert(
            facetValue.lowerBound,
            unit,
            ConversionUnit.Millimetre
        ),
        upperBoundMillimetres: convert(
            facetValue.upperBound,
            unit,
            ConversionUnit.Millimetre
        ),
        initialUnit: unit
    };
};

export const mapToFacetValueWithDisplayValue = (
    facetValue: ReturnType<typeof mapToFacetValueInMillimetres>
) => {
    const preferredUnit = getPreferredUnitForValue(
        facetValue.lowerBoundMillimetres,
        facetValue.initialUnit
    );

    const displayValue = formatUnitRangeLabel(
        {
            ...facetValue,
            lowerBound: convert(
                facetValue.lowerBoundMillimetres,
                ConversionUnit.Millimetre,
                preferredUnit
            ),
            upperBound: convert(
                facetValue.upperBoundMillimetres,
                ConversionUnit.Millimetre,
                preferredUnit
            ),
            unit:
                preferredUnit !== ConversionUnit.Default
                    ? preferredUnit
                    : facetValue.unit
        },
        Config.getLocale()
    );

    return {
        ...facetValue,
        displayValue: displayValue
    };
};
